// @ts-nocheck
/* Port of ReleaseCompareModal.js (151L). BYTE-IDENTICAL.
 * XL react-bootstrap Modal; on show, seeds A/B to first two ids (or
 * first twice as fallback). Fetches via `compareReleases(a, b)` and
 * renders two CATEGORY_ORDER-ordered columns.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { FiX } from 'react-icons/fi';
import { compareReleases } from './releaseCenterService';
import { HIGHLIGHT_CATEGORY_META, formatReleaseDate } from './releaseUtils';
import VersionBadge from './VersionBadge';
import ReleaseTypeBadge from './ReleaseTypeBadge';

const CATEGORY_ORDER = ['Added', 'Improved', 'Fixed', 'Removed'];

const CompareColumn = ({ side }: any) => {
  if (!side?.release) return null;
  const { release, highlights } = side;
  return (
    <div className="rc-compare-col">
      <div className="rc-card__meta">
        <VersionBadge version={release.version} />
        <ReleaseTypeBadge type={release.release_type} />
      </div>
      <h5>{release.title}</h5>
      <div
        className="rc-modal__date"
        style={{ color: 'var(--rc-text-muted)', marginBottom: 10 }}
      >
        {formatReleaseDate(release.release_date)}
      </div>
      {CATEGORY_ORDER.map((cat: any) => {
        const items = highlights?.[cat] || [];
        if (!items.length) return null;
        const meta = HIGHLIGHT_CATEGORY_META[cat];
        return (
          <div className="rc-compare-cat" key={cat}>
            <div
              className="rc-compare-cat__label"
              style={{ color: meta.color }}
            >
              {meta.icon} {cat}
            </div>
            <ul>
              {items.map((label: any, i: number) => (
                <li key={i}>{label}</li>
              ))}
            </ul>
          </div>
        );
      })}
      {CATEGORY_ORDER.every(
        (c: any) => !(highlights?.[c] || []).length,
      ) && (
        <p
          className="rc-richtext"
          style={{ color: 'var(--rc-text-muted)', fontSize: 13 }}
        >
          No categorised highlights for this release.
        </p>
      )}
    </div>
  );
};

const ReleaseCompareModal = ({
  show,
  onHide,
  releases = [],
  initialA,
  initialB,
}: any) => {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setResult(null);
    const first = initialA || releases[0]?.id || '';
    const second = initialB || releases[1]?.id || releases[0]?.id || '';
    setA(first);
    setB(second);
  }, [show, initialA, initialB, releases]);

  const runCompare = async () => {
    if (!a || !b) return;
    setLoading(true);
    try {
      const data: any = await compareReleases(a, b);
      setResult(data);
    } catch {
      /* httpCall toasts */
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className="sm:max-w-4xl rc-modal"
        aria-labelledby="rc-compare-title"
      >
      <div className="rc-modal__hero">
        <button
          type="button"
          className="rc-modal__close"
          onClick={onHide}
          aria-label="Close"
        >
          <FiX size={18} />
        </button>
        <span className="rc-hero__eyebrow"> Compare</span>
        <h2 className="rc-modal__title" id="rc-compare-title">
          Compare releases
        </h2>
        <div className="rc-modal__date">
          See exactly what's different between any two versions.
        </div>
      </div>

      <div className="rc-modal__body">
        <div className="rc-grid-2" style={{ marginBottom: 16 }}>
          <div className="rc-field" style={{ margin: 0 }}>
            <label>Version A</label>
            <select
              className="rc-select"
              value={a}
              onChange={(e) => setA(e.target.value)}
            >
              {releases.map((r: any) => (
                <option key={r.id} value={r.id}>
                  v{r.version} - {r.title}
                </option>
              ))}
            </select>
          </div>
          <div className="rc-field" style={{ margin: 0 }}>
            <label>Version B</label>
            <select
              className="rc-select"
              value={b}
              onChange={(e) => setB(e.target.value)}
            >
              {releases.map((r: any) => (
                <option key={r.id} value={r.id}>
                  v{r.version} - {r.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className="rc-btn rc-btn--primary"
          onClick={runCompare}
          disabled={loading || !a || !b || a === b}
        >
          {loading ? 'Comparing…' : 'Generate comparison'}
        </button>
        {a === b && (
          <span
            style={{
              marginLeft: 12,
              color: 'var(--rc-text-muted)',
              fontSize: 13,
            }}
          >
            Pick two different versions.
          </span>
        )}

        {result && (
          <div className="rc-compare-grid" style={{ marginTop: 22 }}>
            <CompareColumn side={result.a} />
            <CompareColumn side={result.b} />
          </div>
        )}
      </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReleaseCompareModal;
